import React, { type CSSProperties, type ReactNode } from "react";
import Link from "next/link";
import { ArrowRight, type LucideIcon } from "lucide-react";
import { Container } from "./Container";
import { Breadcrumb, type BreadcrumbItem } from "./Breadcrumb";
import { PageHeroImage } from "./PageHeroImage";
import { CardDescriptionHtml } from "@/components/visual-cms/CardDescriptionHtml";

// Standard pill CTA shapes used by every marketing page hero.
export interface PageHeroCta {
  label: string;
  href: string;
}

export type PageHeroAlign = "left" | "center";

export interface PageHeroLayoutProps {
  /** Optional crumb trail rendered above the title. */
  breadcrumb?: BreadcrumbItem[];

  /**
   * Optional eyebrow tag rendered above the heading.
   * Auto-decorated with the brand pill when supplied.
   */
  eyebrow?: string;
  eyebrowIcon?: LucideIcon;

  /** Plain part of the heading. Required. */
  title: string;
  /**
   * Optional accent phrase rendered in brand green after the title.
   * `highlightDisplay` controls whether it sits inline or on a new line.
   */
  titleHighlight?: string;
  highlightDisplay?: "inline" | "block";

  description?: ReactNode;

  primaryCta?: PageHeroCta;
  secondaryCta?: PageHeroCta;
  /**
   * Custom right column slot. Receives the illustration. When omitted, the
   * hero collapses to a single-column layout and centers the text block at
   * `max-w-2xl` so legal pages don't sit awkwardly wide.
   *
   * If `imageUrl` is also supplied, the image takes precedence and this slot
   * is ignored — admins who upload a hero image always win over the default
   * decorative illustration.
   */
  illustration?: ReactNode;

  /**
   * Admin-uploaded hero image URL. When set, the right column renders this
   * image (with `imageAlt`) instead of the `illustration` prop, and a broken
   * URL hides itself silently rather than substituting the illustration —
   * admins choose images deliberately, so a broken URL is a real signal.
   */
  imageUrl?: string;
  imageAlt?: string;

  /**
   * Horizontal alignment of the text column.
   * Defaults to `left` to match the reference layouts. `center` is provided
   * only for legacy pages that haven't been migrated yet.
   */
  align?: PageHeroAlign;

  /**
   * Layout variant — controls how text + illustration columns are arranged.
   *   - "left"          → illustration left, text right
   *   - "right"|default → illustration right, text left (matches reference)
   *   - "center"        → text-only centered layout, illustration hidden
   *
   * Plain string for forward-compat with Visual CMS arbitrary values.
   */
  layoutVariant?: string;

  /**
   * Hero <section> inline overrides — used by the Visual CMS to apply admin
   * background/text colours without touching the surrounding markup.
   */
  containerStyle?: CSSProperties;

  /** Extra utility classes appended to the root <section>. */
  className?: string;
}

/**
 * Canonical hero layout used by every public marketing/legal page.
 *
 * Centralises the structure (background tone, padding, grid, typography
 * scale) so per-page heroes only need to supply content + their illustration.
 * Page-specific heroes (AboutHero, TermsHero, HowItWorksHero, …) should
 * compose this rather than re-implement the chrome.
 */
export const PageHeroLayout: React.FC<PageHeroLayoutProps> = ({
  breadcrumb,
  eyebrow,
  eyebrowIcon: EyebrowIcon,
  title,
  titleHighlight,
  highlightDisplay = "inline",
  description,
  primaryCta,
  secondaryCta,
  illustration,
  imageUrl,
  imageAlt,
  align = "left",
  layoutVariant,
  containerStyle,
  className = "",
}) => {
  // Admin-supplied image overrides any default illustration slot. Both nullish
  // → no right column. The `<img>` lives inside a Client Component so its
  // `onError` handler is allowed in server-rendered hero callers.
  const trimmedImage = imageUrl?.trim() || "";
  const resolvedIllustration = trimmedImage ? (
    <PageHeroImage
      src={trimmedImage}
      alt={imageAlt?.trim() || title || "Hero illustration"}
    />
  ) : (
    illustration ?? null
  );

  const variant = (layoutVariant ?? "").trim().toLowerCase();
  const imageOnLeft = variant === "left";
  const forceCenter = variant === "center";
  const hasIllustration = Boolean(resolvedIllustration) && !forceCenter;
  const centered = forceCenter || align === "center";

  return (
    <section
      className={`relative bg-gradient-to-b from-v2-green-50/60 via-v2-neutral-0 to-v2-neutral-0 pt-12 pb-12 lg:pt-16 lg:pb-14 overflow-hidden ${className}`}
      style={containerStyle}
    >
      {/* Subtle dotted ornament — kept faint so it never competes with content. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-[0.30]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(30,140,58,0.16) 1px, transparent 0)",
          backgroundSize: "22px 22px",
          maskImage: "linear-gradient(to bottom, black, transparent 78%)",
          WebkitMaskImage: "linear-gradient(to bottom, black, transparent 78%)",
        }}
      />

      <Container className="relative">
        {breadcrumb && breadcrumb.length > 0 && (
          <Breadcrumb items={breadcrumb} className="mb-6" />
        )}

        <div
          className={
            hasIllustration
              ? "grid lg:grid-cols-[1.1fr_1fr] items-center gap-10"
              : centered
                ? "max-w-3xl mx-auto text-center"
                : "max-w-3xl"
          }
        >
          <div
            className={`${
              hasIllustration
                ? centered
                  ? "max-w-2xl mx-auto text-center"
                  : "max-w-2xl"
                : ""
            } ${hasIllustration && imageOnLeft ? "lg:order-2" : ""}`}
          >
            {/* {eyebrow && (
              <span
                className={`inline-flex items-center gap-2 px-3 py-1 rounded-v2-full bg-v2-neutral-0 border border-v2-green-200 text-[11px] font-semibold text-v2-green-600 mb-5 shadow-sm`}
              >
                {EyebrowIcon ? <EyebrowIcon size={13} strokeWidth={2.2} /> : null}
                {eyebrow}
              </span>
            )} */}

            <h1 className="font-display text-[34px] sm:text-[42px] lg:text-[48px] font-bold tracking-[-0.02em] leading-[1.06] mb-5 text-v2-neutral-900">
              {title}
              {titleHighlight &&
                (highlightDisplay === "block" ? (
                  <span className="block bg-gradient-to-r from-v2-green-500 to-v2-green-700 bg-clip-text text-transparent">
                    {titleHighlight}
                  </span>
                ) : (
                  <>
                    {title ? " " : null}
                    <span className="bg-gradient-to-r from-v2-green-500 to-v2-green-700 bg-clip-text text-transparent">
                      {titleHighlight}
                    </span>
                  </>
                ))}
            </h1>

            {description &&
              // When a hero hands us a plain string (the common path —
              // admins type their copy into the Visual CMS Description
              // field), pipe it through CardDescriptionHtml so any
              // bold/italic/link markup the rich editor emitted renders as
              // formatted HTML rather than literal `<strong>` tags. Already-
              // composed ReactNodes (e.g. FAQ's "Can't find what you're
              // looking for? <a>" fragment) pass through untouched.
              (typeof description === "string" ? (
                <CardDescriptionHtml
                  as="div"
                  text={description}
                  className={`text-[16px] sm:text-[17px] leading-[1.7] text-v2-neutral-500 max-w-xl ${
                    centered ? "mx-auto" : ""
                  }`}
                />
              ) : (
                <div
                  className={`text-[16px] sm:text-[17px] leading-[1.7] text-v2-neutral-500 max-w-xl ${
                    centered ? "mx-auto" : ""
                  }`}
                >
                  {description}
                </div>
              ))}

            {(primaryCta || secondaryCta) && (
              <div
                className={`mt-7 flex flex-wrap items-center gap-3 ${
                  centered ? "justify-center" : ""
                }`}
              >
                {primaryCta && (
                  <Link
                    href={primaryCta.href}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-v2-full bg-v2-green-600 text-white font-semibold text-[14px] hover:bg-v2-green-700 transition-colors shadow-[0_10px_24px_-12px_rgba(30,140,58,0.6)]"
                  >
                    {primaryCta.label}
                    <ArrowRight size={14} strokeWidth={2.2} />
                  </Link>
                )}
                {secondaryCta && (
                  <Link
                    href={secondaryCta.href}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-v2-full bg-v2-neutral-0 border-[1.5px] border-v2-neutral-200 text-v2-neutral-700 font-semibold text-[14px] hover:bg-v2-neutral-50 hover:border-v2-green-300 transition-colors"
                  >
                    {secondaryCta.label}
                  </Link>
                )}
              </div>
            )}
          </div>

          {hasIllustration && (
            <div
              className={`hidden lg:flex justify-center items-center ${
                imageOnLeft ? "lg:order-1" : ""
              }`}
            >
              {resolvedIllustration}
            </div>
          )}
        </div>
      </Container>
    </section>
  );
};
